// The chat box's history (TOM-915): Up brings back the messages the owner sent
// in the open chat, newest first, and Down walks forward again to the draft
// they were typing. Escape goes straight back to the draft.
//
// Pure, so it tests in Node. ChatComposer keeps the state and applies the
// result to the field.

/** Where the box is in its history. `index` null means it shows the draft. */
export interface InputHistoryState {
  index: number | null;
  /** What was in the box when the owner first pressed Up. */
  draft: string;
}

export const INPUT_HISTORY_START: InputHistoryState = { index: null, draft: '' };

/** The text to put in the box, and where the caret goes. */
export interface InputHistoryStep {
  state: InputHistoryState;
  value: string;
  caret: 'start' | 'end';
}

/**
 * The entries Up walks through, oldest first: the sent messages with blank ones
 * left out, and a message sent twice in a row kept once.
 */
export function historyEntries(sent: readonly string[]): string[] {
  const entries: string[] = [];
  for (const text of sent) {
    if (text.trim().length === 0) continue;
    if (entries.at(-1) === text) continue;
    entries.push(text);
  }
  return entries;
}

/** True when the caret sits on the field's first line, so Up may leave it. */
export function caretOnFirstLine(value: string, selectionStart: number): boolean {
  return !value.slice(0, selectionStart).includes('\n');
}

/** True when the caret sits on the field's last line, so Down may leave it. */
export function caretOnLastLine(value: string, selectionEnd: number): boolean {
  return !value.slice(selectionEnd).includes('\n');
}

/**
 * One step back. Null when there is nothing to bring back. At the oldest entry
 * it stays put. A message of several lines puts the caret at its start, so the
 * next Up keeps going back instead of moving within it.
 */
export function historyBack(
  entries: readonly string[],
  state: InputHistoryState,
  current: string
): InputHistoryStep | null {
  if (entries.length === 0) return null;
  const next =
    state.index === null
      ? { index: entries.length - 1, draft: current }
      : { index: Math.max(0, state.index - 1), draft: state.draft };
  const value = entries[next.index]!;
  return { state: next, value, caret: value.includes('\n') ? 'start' : 'end' };
}

/**
 * One step forward. Null when the box already shows the draft. Past the newest
 * entry it shows the draft again. The caret goes to the end, so the next Down
 * keeps going forward.
 */
export function historyForward(
  entries: readonly string[],
  state: InputHistoryState
): InputHistoryStep | null {
  if (state.index === null) return null;
  if (state.index >= entries.length - 1) {
    return { state: { index: null, draft: '' }, value: state.draft, caret: 'end' };
  }
  const index = state.index + 1;
  return { state: { index, draft: state.draft }, value: entries[index]!, caret: 'end' };
}

/** Escape: straight back to the draft. Null when the box already shows it. */
export function historyExit(state: InputHistoryState): InputHistoryStep | null {
  if (state.index === null) return null;
  return { state: { index: null, draft: '' }, value: state.draft, caret: 'end' };
}
